import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { TABS } from "./config";
import { useFaixaDeAbas } from "./faixa-abas";
import type { Row } from "./types";
import { api, setSessionUser } from "./api";
import { ICONS, IconDashboard, IconSun, IconMoon, IconLogout, IconHome } from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import Dashboard from "./components/Dashboard";
import DesempenhoEquipeView from "./components/DesempenhoEquipeView";
import GestoresSetorView from "./components/GestoresSetorView";
import CrudView from "./components/CrudView";
import AvisoSistema from "./components/AvisoSistema";
import { PermissoesProvider } from "./permissoes";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba,
  primeiraAbaLiberada,
  type Visualizacao,
} from "./modo-leitura";
import {
  SYSTEMS,
  goToSystem,
  readSsoUser,
  clearSsoFromUrl,
  checkAcesso,
  isAdminSupremo,
} from "./integration";

const SESSION_KEY = "sercamp-qa-user";

/**
 * Usuário atual ao iniciar o app: prioriza o SSO vindo do Portal (#sso=...),
 * caindo na sessão salva no localStorage. O token SSO é removido da URL e
 * persistido como sessão para sobreviver a recarregamentos.
 */
function resolveInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    clearSsoFromUrl();
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Estado do portão de acesso (controle de acesso por sistema, via Portal). */
type AccessState = "checking" | "allowed" | "denied" | "error";

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "dashboard" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && TABS.some((t) => t.key === k) ? k : "dashboard";
  } catch {
    return "dashboard";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(resolveInitialUser);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  const [ncs, setNcs] = useState<Row[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Excluir é exclusivo de administrador (regra do Portal): só quem tem isso
  // enxerga os botões de lixeira. Começa em false para não piscar o botão.
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // Portão de acesso por sistema: enquanto não houver usuário, fica "checking"
  // para não piscar nem o login nem o app antes da decisão do Portal.
  const [access, setAccess] = useState<AccessState>("checking");
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, access === "allowed");
  // Permite refazer a checagem (ex.: botão "Tentar novamente" após falha de rede).
  const [accessAttempt, setAccessAttempt] = useState(0);
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);

  // Portão de acesso: sempre que houver usuário atual (SSO novo ou sessão
  // restaurada), pergunta ao Portal se ele pode usar este sistema antes de
  // liberar o app. O Admin Supremo é sempre permitido (inclusive sem cadastro).
  useEffect(() => {
    if (!user) {
      setAccess("checking");
      return;
    }
    let cancel = false;
    setAccess("checking");
    // Admin Supremo: liberação imediata, mesmo que o Portal não responda.
    if (isAdminSupremo(user)) {
      setAccess("allowed");
      return;
    }
    checkAcesso(user)
      .then((r) => {
        if (cancel) return;
        setVis({ somenteLeitura: r.somenteLeitura, abas: r.abas });
        if (r.allowed || r.adminSupremo) setAccess("allowed");
        else {
          // Bloqueado: anula a sessão para não reentrar com ela.
          localStorage.removeItem(SESSION_KEY);
          encerrarSessao();
          setAccess("denied");
        }
      })
      .catch(() => {
        // Falha de rede: fail-safe, não libera o app.
        if (!cancel) setAccess("error");
      });
    return () => {
      cancel = true;
    };
  }, [user, accessAttempt]);

  // Resolve se o usuário logado é administrador (define quem vê/gerencia "Usuários").
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      return;
    }
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
      })
      .catch(() => {
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
      });
  }, [user]);

  // Não-admin não acessa a aba de usuários (nem por deep-link).
  useEffect(() => {
    if (active === "usuarios" && !isAdmin) setActive("dashboard");
  }, [active, isAdmin]);

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(access === "allowed" && vis.somenteLeitura);
  }, [access, vis.somenteLeitura]);

  // Aba fora das liberadas (inclusive por deep-link) cai na primeira liberada.
  const verAba = (k: string) =>
    podeVerAba(vis, k) && !(vis.somenteLeitura && (k === "usuarios" || k === "solicitar_atualizacao"));
  useEffect(() => {
    if (!verAba(active)) {
      const primeira = primeiraAbaLiberada(vis, ["dashboard", ...TABS.map((t) => t.key)].filter(verAba));
      if (primeira) setActive(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vis]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  const loadNcs = useCallback(async () => {
    try {
      setNcs(await api.list("nao_conformidades"));
    } catch {
      /* mostrado nas telas individuais */
    }
  }, []);

  useEffect(() => {
    if (user) loadNcs();
  }, [user, loadNcs]);

  const onDataChanged = useCallback(() => {
    loadNcs();
    setRefreshKey((k) => k + 1);
  }, [loadNcs]);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setAccess("checking");
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // Enquanto o Portal decide o acesso, não pisca o app.
  if (access === "checking") {
    return (
      <div className="access-gate">
        <AnimatedBackground />
        <div className="access-card">
          <img src={logoBranca} alt="SERCAMP" className="access-logo" />
          <div className="access-spinner" aria-hidden="true" />
          <p className="access-msg">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  // Acesso negado: tela cheia, sem renderizar o app nem manter sessão ativa.
  if (access === "denied") {
    return (
      <div className="access-gate">
        <AnimatedBackground />
        <div className="access-card">
          <img src={logoBranca} alt="SERCAMP" className="access-logo" />
          <h1 className="access-title">Acesso negado</h1>
          <p className="access-msg">
            Você não tem acesso a este sistema. Fale com o Admin Supremo.
          </p>
          <button
            className="btn-primary access-btn"
            onClick={() => goToSystem(SYSTEMS.portal, user)}
          >
            Voltar ao Portal
          </button>
        </div>
      </div>
    );
  }

  // Falha ao falar com o Portal: não libera, mas oferece tentar de novo.
  if (access === "error") {
    return (
      <div className="access-gate">
        <AnimatedBackground />
        <div className="access-card">
          <img src={logoBranca} alt="SERCAMP" className="access-logo" />
          <h1 className="access-title">Não foi possível verificar o acesso</h1>
          <p className="access-msg">
            Não conseguimos confirmar sua permissão com o Portal. Verifique sua
            conexão e tente novamente.
          </p>
          <div className="access-actions">
            <button
              className="btn-primary access-btn"
              onClick={() => {
                setAccess("checking");
                setAccessAttempt((n) => n + 1);
              }}
            >
              Tentar novamente
            </button>
            <button
              className="theme-toggle access-btn-alt"
              onClick={() => goToSystem(SYSTEMS.portal, user)}
            >
              Voltar ao Portal
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tab = TABS.find((t) => t.key === active);
  const title = active === "dashboard" ? "Dashboard" : tab?.label ?? "";
  const subtitle =
    active === "dashboard"
      ? "Visão geral dos indicadores da qualidade"
      : tab?.subtitle ?? "";

  return (
    <PermissoesProvider value={podeExcluir}>
      <AvisoSistema />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>

          <nav className="nav" ref={navRef}>
            {verAba("dashboard") && (
              <button
                className={`nav-item ${active === "dashboard" ? "active" : ""}`}
                onClick={() => setActive("dashboard")}
              >
                <span className="nav-icon">
                  <IconDashboard size={19} />
                </span>
                Dashboard
              </button>
            )}
            {TABS.filter((t) => (t.key !== "usuarios" || isAdmin) && verAba(t.key)).map((t) => {
              const Icon = ICONS[t.icon];
              return (
                <button
                  key={t.key}
                  className={`nav-item ${active === t.key ? "active" : ""}`}
                  onClick={() => setActive(t.key)}
                >
                  <span className="nav-icon">
                    <Icon size={19} />
                  </span>
                  {t.label}
                </button>
              );
            })}
          </nav>

        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{title}</h1>
              <p className="muted">{subtitle}</p>
            </div>

            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <span className="system-badge">Qualidade</span>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">
                  {user.charAt(0).toUpperCase()}
                </span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          <section className="view">
            {!verAba(active) ? null : active === "dashboard" ? (
              <Dashboard refreshKey={refreshKey} />
            ) : active === "desempenho" ? (
              <DesempenhoEquipeView />
            ) : active === "gestores_setor" ? (
              <GestoresSetorView />
            ) : tab?.key === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido vai para o
              // sistema de Desenvolvimento (ver server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : tab ? (
              <CrudView key={tab.key} tab={tab} ncs={ncs} onDataChanged={onDataChanged} />
            ) : null}
          </section>
        </main>
      </div>
    </PermissoesProvider>
  );
}
